/**
 * Validates a standard 17-digit Vehicle Identification Number (VIN).
 * Excludes I, O, and Q.
 */
export const validateVIN = (vin: string): boolean => {
  if (!vin) return false;
  const cleanVIN = vin.trim().toUpperCase();
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
  return vinRegex.test(cleanVIN);
};

/**
 * Validates a Canadian Postal Code.
 * Format: A1A 1A1 or A1A1A1 (case-insensitive).
 */
export const validateCanadianPostalCode = (postalCode: string): boolean => {
  if (!postalCode) return false;
  const cleanPostalCode = postalCode.trim().toUpperCase();
  const postalCodeRegex = /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/;
  return postalCodeRegex.test(cleanPostalCode);
};

/**
 * Validates a Phone Number.
 * Must be at least 10 digits (allowing formatting chars).
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone) return false;
  const cleanPhone = phone.replace(/\D/g, "");
  return cleanPhone.length >= 10;
};

export type DealStage = 'prospect' | 'negotiation' | 'financed' | 'delivered' | 'funded' | 'lost';

/**
 * Validates if a transition from currentStage to nextStage is allowed.
 */
export const validateDealStageTransition = (currentStage: DealStage, nextStage: DealStage): { allowed: boolean; reason?: string } => {
  if (currentStage === nextStage) return { allowed: true };

  // Terminal stages cannot be changed
  if (currentStage === 'funded') {
    return { allowed: false, reason: "A funded deal cannot be modified or reverted." };
  }
  
  if (currentStage === 'lost' && nextStage !== 'negotiation') {
    return { allowed: false, reason: "A lost deal can only be reverted to Negotiation to restart processing." };
  }

  if (currentStage === 'delivered' && nextStage !== 'funded' && nextStage !== 'lost') {
    return { allowed: false, reason: "A delivered deal can only progress to Funded or change to Lost." };
  }

  // General flows
  if (currentStage === 'prospect' && nextStage !== 'negotiation' && nextStage !== 'lost') {
    return { allowed: false, reason: "Prospect deals can only transition to Negotiation or Lost." };
  }

  return { allowed: true };
};
